import React from 'react'
import { Button as ShadcnButton } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const variantMap: Record<string, 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  success: 'default',
  info: 'default',
  warning: 'default',
  light: 'ghost',
  dark: 'secondary',
  link: 'link',
}

const colorClassMap: Record<string, string> = {
  success: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]/90',
  info: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]/90',
  warning: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]/90',
}

const outlineColorClassMap: Record<string, string> = {
  primary: 'border-primary text-primary hover:bg-primary/10',
  danger: 'border-destructive text-destructive hover:bg-destructive/10',
  success: 'border-[hsl(var(--success))] text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10',
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  color?: string
  size?: string
  outline?: boolean
  block?: boolean
  active?: boolean
  tag?: React.ElementType
  innerRef?: React.Ref<HTMLButtonElement>
  close?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ color = 'primary', size: bsSize, outline, block, className, children, tag, innerRef, close, ...props }, ref) => {
    // Parse "outline-primary" format
    let isOutline = outline || false
    let baseColor = color
    if (color.startsWith('outline-')) {
      isOutline = true
      baseColor = color.replace('outline-', '')
    }

    const variant = isOutline ? 'outline' : (variantMap[baseColor] || 'default')
    const size = bsSize === 'sm' ? 'sm' : bsSize === 'lg' ? 'lg' : 'default'
    const colorClass = isOutline
      ? outlineColorClassMap[baseColor]
      : colorClassMap[baseColor]

    return (
      <ShadcnButton
        ref={innerRef || ref}
        variant={variant}
        size={size}
        className={cn(
          colorClass,
          block && 'w-full',
          className,
        )}
        {...props}
      >
        {children}
      </ShadcnButton>
    )
  },
)

Button.displayName = 'Button'
